// hooks/useGoerOneAuth.js
// Secondary "GoerOne" customer auth — separate from the app's main AuthContext
// (which authenticates admin/staff users). Every /web/* customer-facing page
// (Landing, StoreDetailPage, StorePayment, ...) should read the logged-in test
// customer through this hook instead of each duplicating its own
// sessionStorage("goerone_test_user"/"goerone_test_token") read.
import { useCallback, useEffect, useRef, useState } from "react";
import { goeroneTestLogin } from "../services/api";

const TOKEN_KEY = "goerone_test_token";
const USER_KEY = "goerone_test_user";

function readToken() {
    try {
        return sessionStorage.getItem(TOKEN_KEY) || null;
    } catch {
        return null;
    }
}

function readUser() {
    try {
        const saved = sessionStorage.getItem(USER_KEY);
        return saved ? JSON.parse(saved) : null;
    } catch {
        return null;
    }
}

function persist(token, user) {
    try {
        if (token) sessionStorage.setItem(TOKEN_KEY, token);
        if (user) sessionStorage.setItem(USER_KEY, JSON.stringify(user));
    } catch {
        /* storage unavailable - session just won't survive a refresh */
    }
}

function clearStored() {
    try {
        sessionStorage.removeItem(TOKEN_KEY);
        sessionStorage.removeItem(USER_KEY);
    } catch {
        /* ignore */
    }
}

/**
 * bootstrap: { id, mobile } - only Landing.jsx (the page opened from Customer.jsx's
 * "i" icon) needs this; it's the one that starts the GoerOne test-login session via
 * POST /api/GoerOne/login. Every other /web page just calls useGoerOneAuth() with no
 * arguments and reads the session Landing already created for that tab.
 *
 * Returns:
 *   user        - the logged-in test customer (user.user_membership carries membership
 *                 state - see utils/membershipStatus.js), or null if not logged in yet
 *   token       - the GoerOne test-login access token, or null
 *   ready       - true once we know whether a session exists (bootstrap has settled,
 *                 or there was nothing to bootstrap)
 *   loggingIn   - true while the one-time test-login call is in flight
 *   loginError  - message from a failed bootstrap login, if any (caller decides how
 *                 to surface it, e.g. a toastr.error)
 *   updateUser(patch) - merge a partial update (object, or updater function) into the
 *                 stored user and persist it - e.g. right after a membership purchase,
 *                 before navigating to the next page
 *   logout()    - clear the GoerOne test session
 */
export default function useGoerOneAuth(bootstrap) {

    const [token, setToken] = useState(readToken);
    const [user, setUser] = useState(readUser);
    const [loggingIn, setLoggingIn] = useState(false);
    const [loginError, setLoginError] = useState("");
    const attempted = useRef(false);
    const userRef = useRef(user);

    useEffect(() => { userRef.current = user; }, [user]);

    const updateUser = useCallback((patch) => {
        setUser((prev) => {
            const next = typeof patch === "function" ? patch(prev) : { ...prev, ...patch };
            persist(null, next);
            return next;
        });
    }, []);

    // Re-fetch the customer (e.g. after buying a membership) and persist it,
    // so every page that reads the session sees the new user_membership.
    const refreshUser = useCallback(async () => {
        const u = userRef.current;
        const mobile = u?.mobile ?? u?.phone;
        if (!u?.id || !mobile) return null;
        try {
            const res = await goeroneTestLogin({
                mobile,
                GoerOne: true,
                test_login: true,
                customer_id: Number(u.id),
            });
            if (res?.access_token && res?.user) {
                persist(res.access_token, res.user);
                setToken(res.access_token);
                setUser(res.user);
                return res.user;
            }
        } catch {
            /* keep the existing session if the refresh fails */
        }
        return null;
    }, []);

    const logout = useCallback(() => {
        clearStored();
        setToken(null);
        setUser(null);
        attempted.current = false;
    }, []);

    useEffect(() => {
        if (!bootstrap?.id || !bootstrap?.mobile) return;
        if (token || user || attempted.current) return;
        attempted.current = true;
        let cancelled = false;
        setLoggingIn(true);
        setLoginError("");
        (async () => {
            try {
                const res = await goeroneTestLogin({
                    mobile: bootstrap.mobile,
                    GoerOne: true,
                    test_login: true,
                    customer_id: Number(bootstrap.id),
                });
                if (cancelled) return;
                if (res?.access_token) {
                    persist(res.access_token, res.user);
                    setToken(res.access_token);
                    setUser(res.user);
                } else {
                    setLoginError(res?.message || "Unable to start test session");
                }
            } catch (err) {
                if (!cancelled) setLoginError(err?.message || "Unable to start test session");
            } finally {
                if (!cancelled) setLoggingIn(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [bootstrap?.id, bootstrap?.mobile, token, user]);

    return { user, token, ready: !!(token || user), loggingIn, loginError, updateUser, refreshUser, logout };
}